import { t, type AsyncState } from '@bms2/shared';
import { AlertBanner } from '../controls/AlertBanner';

/**
 * 🔒 A failed read says so and offers the read again (piece 4; T178). Twenty
 * pages wrote this same ternary by hand — the banner, the error the read
 * answered with, and a retry action labelled from the one string — and one of
 * them had it wrong, drawing the empty line instead, so an unreachable API read
 * as nothing to show (found in review, 2026-09-15).
 *
 * 🔨 It draws nothing at all unless the read failed, so a page places it once
 * above its content and stops writing the test. It is not the async panel: the
 * panel owns a whole region and replaces its content, while this is a banner
 * over content the page draws anyway.
 */
export interface LoadFailureProps<T> {
  /** The read's own state; every kind but `failed` draws nothing. */
  state: AsyncState<T>;
  onRetry: () => void;
}

export function LoadFailure<T>({ state, onRetry }: LoadFailureProps<T>) {
  if (state.kind !== 'failed') return null;
  return <AlertBanner tone="error" message={state.error} action={{ label: t('async.retry'), onAction: onRetry }} />;
}
